import { useEffect, useRef, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { LifeBuoy, X, Send, BookOpen, Settings2, Sparkles, RotateCcw } from "lucide-react";

const BIENVENIDA = {
  rol: "asistente",
  texto: "Hola. Soy la Ayuda del sistema: te explico cómo usar cada pantalla de Developware, paso a paso.\n\nNo veo tus números ni tus clientes, y no modifico nada. Pregúntame con tus palabras o elige un tema.",
};

function Burbuja({ m }) {
  const mio = m.rol === "usuario";
  return (
    <div className={`flex ${mio ? "justify-end" : "justify-start"}`} data-testid={`ayuda-msg-${m.rol}`}>
      <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-line leading-relaxed ${
        mio ? "bg-[#002B49] text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-700 rounded-bl-sm"}`}>
        {m.texto.split("**").map((p, i) => (i % 2 ? <b key={i}>{p}</b> : p))}
        {m.fuentes?.length > 0 && (
          <p className="mt-2 pt-2 border-t border-slate-100 text-[10px] text-slate-400 flex items-start gap-1">
            <BookOpen size={11} className="mt-0.5 flex-shrink-0" />
            <span>Manual: {m.fuentes.map((f) => f.titulo).join(" · ")}</span>
          </p>
        )}
      </div>
    </div>
  );
}

function ConfigAyuda({ onCambio }) {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/ayuda/config").then((r) => setC(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  if (!c) return <p className="text-sm text-slate-400 p-4">Cargando configuración…</p>;
  const guardar = async (cambios) => {
    try {
      const r = await api.put("/ayuda/config", cambios);
      setC(r.data);
      onCambio();
      toast.success("Configuración guardada");
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="p-4 space-y-5 overflow-y-auto" data-testid="ayuda-config">
      <div className="flex items-start justify-between gap-4 border rounded-xl p-3">
        <div>
          <p className="text-sm font-bold text-[#002B49]">Ayuda IA encendida</p>
          <p className="text-xs text-slate-500">Si la apagas, nadie de la empresa verá este chat.</p>
        </div>
        <Switch checked={c.ayuda_ia.activo} onCheckedChange={(v) => guardar({ activo: v })} data-testid="ayuda-switch-activo" />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Modelo que usa el chat</Label>
        <Select value={c.ayuda_ia.modelo} onValueChange={(v) => guardar({ modelo: v })}>
          <SelectTrigger data-testid="ayuda-modelo-select"><SelectValue /></SelectTrigger>
          <SelectContent>{c.modelos.map((m) => <SelectItem key={m.clave} value={m.clave}>{m.nombre}</SelectItem>)}</SelectContent>
        </Select>
        <p className="text-[11px] text-slate-400">Para ayuda conviene un modelo rápido y económico. El manual tiene {c.manual_secciones} temas cargados.</p>
      </div>

      <div>
        <p className="text-sm font-bold text-[#002B49] mb-1">Módulos de la empresa</p>
        <p className="text-xs text-slate-500 mb-2">El chat no habla de los módulos apagados.</p>
        <div className="space-y-1.5">
          {c.catalogo.filter((m) => m.clave !== "ayuda_ia").map((m) => (
            <div key={m.clave} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-1.5">
              <span className="text-xs text-slate-600">{m.nombre}</span>
              <Switch checked={c.modulos[m.clave]} data-testid={`ayuda-switch-${m.clave}`}
                onCheckedChange={(v) => guardar({ modulos: { [m.clave]: v } })} />
            </div>))}
        </div>
      </div>
    </div>
  );
}

export default function AyudaIA() {
  const { user } = useAuth();
  const [estado, setEstado] = useState(null);
  const [abierto, setAbierto] = useState(false);
  const [tab, setTab] = useState("chat");
  const [msgs, setMsgs] = useState([BIENVENIDA]);
  const [texto, setTexto] = useState("");
  const [cargando, setCargando] = useState(false);
  const sid = useRef(null);
  const fin = useRef(null);

  const cargarEstado = () => api.get("/ayuda/estado").then((r) => setEstado(r.data)).catch(() => setEstado(null));
  useEffect(() => { if (user) cargarEstado(); }, [user]);
  useEffect(() => { fin.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, abierto]);

  if (!user || !estado?.activo) return null;

  const preguntar = async (pregunta) => {
    const q = (pregunta || texto).trim();
    if (!q || cargando) return;
    setMsgs((m) => [...m, { rol: "usuario", texto: q }]);
    setTexto("");
    setCargando(true);
    try {
      const r = await api.post("/ayuda/chat", { mensaje: q, session_id: sid.current });
      sid.current = r.data.session_id;
      setMsgs((m) => [...m, { rol: "asistente", texto: r.data.respuesta, fuentes: r.data.fuentes }]);
    } catch (e) {
      setMsgs((m) => [...m, { rol: "asistente", texto: apiError(e) }]);
    }
    setCargando(false);
  };

  const reiniciar = () => { sid.current = null; setMsgs([BIENVENIDA]); };

  return (
    <>
      {!abierto && (
        <button onClick={() => setAbierto(true)} data-testid="ayuda-boton-flotante"
          className="fixed bottom-12 right-5 z-[70] flex items-center gap-2 bg-[#002B49] hover:bg-[#00395f] text-white pl-3.5 pr-4 py-3 rounded-full shadow-2xl border border-[#00A3B5]/40 transition-transform hover:-translate-y-0.5">
          <LifeBuoy size={18} className="text-[#00A3B5]" />
          <span className="text-xs font-bold tracking-wide hidden sm:inline">¿Cómo se usa?</span>
        </button>
      )}

      {abierto && (
        <div data-testid="ayuda-panel"
          className="fixed bottom-12 right-3 sm:right-5 z-[70] w-[calc(100vw-1.5rem)] sm:w-[420px] h-[min(78vh,620px)] bg-slate-50 rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
          <div className="bg-[#002B49] px-4 py-3 flex items-center gap-2">
            <Sparkles size={16} className="text-[#00A3B5]" />
            <div className="flex-1">
              <p className="text-white font-display font-bold text-sm leading-tight">Ayuda IA</p>
              <p className="text-white/45 text-[10px]">Te enseña a usar el sistema · no ve tus datos</p>
            </div>
            {estado.es_admin && (
              <button onClick={() => setTab(tab === "config" ? "chat" : "config")} data-testid="ayuda-tab-config"
                className={`p-1.5 rounded-md ${tab === "config" ? "bg-white/20 text-white" : "text-white/50 hover:text-white"}`} title="Configuración">
                <Settings2 size={15} />
              </button>
            )}
            <button onClick={reiniciar} className="p-1.5 rounded-md text-white/50 hover:text-white" title="Empezar de nuevo" data-testid="ayuda-reiniciar">
              <RotateCcw size={15} />
            </button>
            <button onClick={() => setAbierto(false)} className="p-1.5 rounded-md text-white/50 hover:text-white" data-testid="ayuda-cerrar">
              <X size={16} />
            </button>
          </div>

          {tab === "config" ? <ConfigAyuda onCambio={cargarEstado} /> : (
            <>
              <div className="flex-1 overflow-y-auto p-3 space-y-2.5" data-testid="ayuda-mensajes">
                {msgs.map((m, i) => <Burbuja key={i} m={m} />)}
                {cargando && <p className="text-xs text-slate-400 pl-2" data-testid="ayuda-escribiendo">Buscando en el manual…</p>}
                <div ref={fin} />
              </div>

              {msgs.length <= 1 && (
                <div className="px-3 pb-2 flex flex-wrap gap-1.5" data-testid="ayuda-temas">
                  {estado.temas.map((t) => (
                    <button key={t.pregunta} onClick={() => preguntar(t.pregunta)} data-testid={`ayuda-tema-${t.modulo}`}
                      className="text-[11px] border border-slate-300 bg-white hover:border-[#00A3B5] hover:text-[#002B49] text-slate-500 rounded-full px-2.5 py-1 transition-colors">
                      {t.pregunta}
                    </button>))}
                </div>
              )}

              <form onSubmit={(e) => { e.preventDefault(); preguntar(); }}
                className="p-3 border-t border-slate-200 bg-white flex items-center gap-2">
                <Input value={texto} onChange={(e) => setTexto(e.target.value)} data-testid="ayuda-input"
                  placeholder="Escribe tu duda, por ejemplo: ¿cómo registro un pago?" className="text-sm" />
                <Button type="submit" disabled={cargando || !texto.trim()} data-testid="ayuda-enviar"
                  className="bg-[#00A3B5] hover:bg-[#008d9c] text-white h-9 w-9 p-0 flex-shrink-0">
                  <Send size={15} />
                </Button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}
